import type { AppData } from './types';

export interface WeeklyDigest {
  thisWeekSpending: number;
  lastWeekSpending: number;
  changePercent: number;
  changeDirection: 'up' | 'down' | 'same';
  topCategory: string | null;
  topCategoryAmount: number;
  encouragement: string;
  streakDays: number; // consecutive days with logged transactions
}

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1; // Monday start
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function calculateWeeklyDigest(data: AppData): WeeklyDigest {
  const now = new Date();
  const thisWeekStart = startOfWeek(now);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const expenseTxns = data.transactions.filter(t => t.type === 'expense');

  const thisWeek = expenseTxns.filter(t => {
    const d = new Date(t.date);
    return d >= thisWeekStart && d <= now;
  });

  const lastWeek = expenseTxns.filter(t => {
    const d = new Date(t.date);
    return d >= lastWeekStart && d < thisWeekStart;
  });

  const thisWeekSpending = thisWeek.reduce((s, t) => s + t.amount, 0);
  const lastWeekSpending = lastWeek.reduce((s, t) => s + t.amount, 0);

  let changePercent = 0;
  let changeDirection: 'up' | 'down' | 'same' = 'same';
  if (lastWeekSpending > 0) {
    changePercent = Math.round(Math.abs(thisWeekSpending - lastWeekSpending) / lastWeekSpending * 100);
    if (thisWeekSpending < lastWeekSpending) changeDirection = 'down';
    else if (thisWeekSpending > lastWeekSpending) changeDirection = 'up';
  }

  // Top category this week
  const catTotals = new Map<string, number>();
  for (const t of thisWeek) {
    catTotals.set(t.category, (catTotals.get(t.category) ?? 0) + t.amount);
  }
  let topCategory: string | null = null;
  let topCategoryAmount = 0;
  for (const [cat, amt] of catTotals) {
    if (amt > topCategoryAmount) { topCategory = cat; topCategoryAmount = amt; }
  }

  // Streak: consecutive days with any logged transaction
  let streakDays = 0;
  for (let d = new Date(now); ; d.setDate(d.getDate() - 1)) {
    const ds = d.toISOString().slice(0, 10);
    if (data.transactions.some(t => t.date.slice(0, 10) === ds)) {
      streakDays++;
    } else break;
    if (streakDays > 365) break;
  }

  // Pick encouragement
  let encouragement: string;
  if (changeDirection === 'down' && changePercent >= 10) {
    encouragement = `Great work! You spent ${changePercent}% less than last week 🎉`;
  } else if (changeDirection === 'same' || changePercent < 5) {
    encouragement = 'Steady spending this week — consistency is key! 💪';
  } else if (changeDirection === 'up' && changePercent >= 30) {
    encouragement = `Spending jumped ${changePercent}% this week — check where it went 👀`;
  } else if (changeDirection === 'up') {
    encouragement = `Spending up ${changePercent}% — small changes add up! 🤔`;
  } else {
    encouragement = `You saved ${changePercent}% compared to last week — nice! 👏`;
  }

  return {
    thisWeekSpending,
    lastWeekSpending,
    changePercent,
    changeDirection,
    topCategory,
    topCategoryAmount,
    encouragement,
    streakDays,
  };
}
